'use client';

import { useEffect, useState } from 'react';
import { Laboratory } from '@medprev-web-app/api-graphql-client';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { LaboratoryErrorFallback } from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_components/laboratory-error-fallback.component';
import { getLaboratoryScheduleFromSearchApi } from '@/app/(main)/(pages)/(institucional)/(index)/laboratorios/_services/get-laboratory-schedule-from-search-api.service';
import { ArrivalOrderModal } from '@/app/(main)/(pages)/_components/schedules/arrival-order/arrival-order-modal.component';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { resolveLaboratoryName } from '@/utils/laboratory/laboratory.util';

interface LaboratorySchedulesModalProps {
  laboratory: Laboratory;
  procedureIds: string[];
  total: number;
  open: boolean;
  onOpenChange(open: boolean): void;
}

export function LaboratorySchedulesModal({
  laboratory,
  procedureIds,
  total,
  open,
  onOpenChange,
}: LaboratorySchedulesModalProps) {
  const facilityId = laboratory.id ?? '';
  const procedureIdsKey = JSON.stringify(procedureIds);

  const [scheduleId, setScheduleId] = useState<string | undefined>();
  const [partnerId, setPartnerId] = useState<string | undefined>();
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);
  const [hasScheduleError, setHasScheduleError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [previousOpen, setPreviousOpen] = useState(open);

  if (open !== previousOpen) {
    setPreviousOpen(open);
    setScheduleId(undefined);
    setPartnerId(undefined);
    setHasScheduleError(false);
    setIsLoadingSchedule(open && Boolean(facilityId));
  }

  useEffect(() => {
    if (!open || !facilityId) {
      return;
    }

    let cancelled = false;

    setIsLoadingSchedule(true);
    setScheduleId(undefined);
    setPartnerId(undefined);
    setHasScheduleError(false);

    getLaboratoryScheduleFromSearchApi({ facilityId, procedureIds: JSON.parse(procedureIdsKey) as string[] })
      .then((result) => {
        if (!cancelled) {
          setScheduleId(result.scheduleId ?? undefined);
          setPartnerId(result.partnerId ?? undefined);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHasScheduleError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingSchedule(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, facilityId, procedureIdsKey, reloadToken]);

  return (
    <ArrivalOrderModal
      scheduleId={scheduleId}
      entityName={resolveLaboratoryName(laboratory)}
      entityAvatar={laboratory.main?.avatar}
      entityType={EntityTypeEnum.LABORATORY_COLLECT_POINT}
      partnerId={partnerId ?? ''}
      price={total}
      open={open}
      slotFilters={{}}
      isLoadingSchedule={isLoadingSchedule}
      errorContent={hasScheduleError
        ? (
            <LaboratoryErrorFallback
              message="Não foi possível carregar os horários."
              laboratoryId={facilityId}
              dataTestId={DataTestIdEnum.BRANCH_LABORATORY_SCHEDULES_MODAL_ERROR}
              onRetry={() => setReloadToken((current) => current + 1)}
            />
          )
        : undefined}
      dataTestId={DataTestIdEnum.BRANCH_LABORATORY_SCHEDULES_MODAL}
      onOpenChange={onOpenChange}
    />
  );
}
